import { zodResolver } from '@hookform/resolvers/zod';
import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Platform, View } from 'react-native';
import MaskInput from 'react-native-mask-input';
import { z } from 'zod';

import { ControlledOptionRows, FieldBlock } from '@/components/services/wizard/field';
import { sendInviteOnWhatsapp } from '@/components/team/invite-share';
import { SheetStepFooter, TeamSheet } from '@/components/team/team-sheet';
import { ThemedText } from '@/components/themed-text';
import { Input } from '@/components/ui/input';
import { useAddProfessional, useInviteLink } from '@/hooks/use-team';
import { useTerms } from '@/hooks/use-terms';
import { showPlanLimit } from '@/store/planLimitStore';
import { MemberSetup, TeamMember } from '@/types/organization';
import { getApiErrorMessage } from '@/utils/api-error';
import { brazilPhoneMask, formatBrazilPhone, getBrazilPhoneError } from '@/utils/brazil-phone';
import { parsePlanLimitError } from '@/utils/parse-plan-limit-error';
import { createThemedStyles } from '@/hooks/use-theme-colors';

const schema = z.object({
  name: z.string().trim().min(2, 'Escreva o nome.').max(80, 'Nome longo demais.'),
  phone: z.string().superRefine((value, ctx) => {
    const error = getBrazilPhoneError(value);
    if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
  }),
  setup: z.custom<MemberSetup>(
    (v) => v === 'COPY_FROM_OWNER' || v === 'EMPTY',
    'Escolha como começar.',
  ),
});

type Form = z.infer<typeof schema>;

const EMPTY: Partial<Form> = { name: '', phone: '', setup: undefined };

const usePhoneInputStyle = createThemedStyles((th) => ({
  height: 48,
  width: '100%' as const,
  borderRadius: 12,
  borderWidth: 1,
  borderColor: th('#E5E5E5'),
  backgroundColor: th('#FFFFFF'),
  paddingHorizontal: 16,
  fontSize: 16,
  color: th('#0A0A0A'),
  borderCurve: 'continuous' as const,
}));

/**
 * Adicionar profissional: nome + WhatsApp → como começar → convite. O perfil
 * nasce usável (já dá para marcar para ele); o convite é só para ele assumir
 * o login. Faltou assento no plano: fecha e abre o aviso de plano.
 */
export function AddProfessionalSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const phoneInputStyle = usePhoneInputStyle();
  const terms = useTerms();
  const addProfessional = useAddProfessional();
  const inviteLink = useInviteLink();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [created, setCreated] = useState<TeamMember | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { control, trigger, handleSubmit, reset } = useForm<Form>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!open) return;
    reset(EMPTY);
    setStep(1);
    setCreated(null);
    setError(null);
  }, [open, reset]);

  const p = (word: string) => terms.agree(word, 'professional');

  const toStep2 = async () => {
    const ok = await trigger(['name', 'phone']);
    if (ok) setStep(2);
  };

  const onCreate = async (form: Form) => {
    setError(null);
    try {
      const member = await addProfessional.mutateAsync({
        name: form.name.trim(),
        phone: form.phone,
        setup: form.setup,
      });
      setCreated(member);
      setStep(3);
    } catch (err) {
      // Sem assento: o aviso de plano é da raiz, atrás deste Modal — fecha antes.
      if (parsePlanLimitError(err)) {
        onClose();
        showPlanLimit(err);
        return;
      }
      setError(
        getApiErrorMessage(err, `Não foi possível adicionar ${p('o')} ${terms.professional}.`),
      );
    }
  };

  const onInvite = async () => {
    if (!created) return;
    setError(null);
    try {
      const link = await inviteLink.mutateAsync(created.id);
      await sendInviteOnWhatsapp(created.invitePhone, link.message);
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível gerar o convite agora.'));
    }
  };

  const busy = addProfessional.isPending || inviteLink.isPending;

  const footer =
    step === 1 ? (
      <SheetStepFooter onNext={toStep2} nextLabel="Continuar" busy={busy} error={error} />
    ) : step === 2 ? (
      <SheetStepFooter
        onBack={() => setStep(1)}
        onNext={handleSubmit(onCreate)}
        nextLabel="Adicionar"
        busy={busy}
        error={error}
      />
    ) : (
      <SheetStepFooter
        onBack={onClose}
        backLabel="Depois"
        onNext={onInvite}
        nextLabel="Enviar convite"
        busy={busy}
        error={error}
      />
    );

  return (
    <TeamSheet
      open={open}
      onClose={onClose}
      busy={busy}
      title={step === 3 ? 'Pronto' : `Adicionar ${terms.professional}`}
      subtitle={step === 3 ? undefined : `${step} de 2`}
      footer={footer}
    >
      <View className="gap-4" style={{ display: step === 1 ? 'flex' : 'none' }}>
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <FieldBlock title="Nome" error={fieldState.error?.message}>
              <Input
                value={field.value ?? ''}
                onChangeText={field.onChange}
                onBlur={field.onBlur}
                placeholder="Nome e sobrenome"
                autoCapitalize="words"
                style={Platform.OS === 'web' ? ({ outline: 'none' } as never) : undefined}
              />
            </FieldBlock>
          )}
        />
        <Controller
          control={control}
          name="phone"
          render={({ field, fieldState }) => (
            <FieldBlock
              title="WhatsApp"
              note="O convite vai para este número"
              error={fieldState.error?.message}
            >
              <MaskInput
                value={field.value ?? ''}
                onChangeText={(_masked, unmasked) => field.onChange(unmasked)}
                onBlur={field.onBlur}
                mask={brazilPhoneMask}
                keyboardType="phone-pad"
                placeholder="(11) 99999-9999"
                placeholderTextColor="#A3A3A3"
                style={[
                  phoneInputStyle,
                  Platform.OS === 'web' ? ({ outline: 'none' } as never) : null,
                ]}
              />
            </FieldBlock>
          )}
        />
      </View>

      <View style={{ display: step === 2 ? 'flex' : 'none' }}>
        <ControlledOptionRows<Form, MemberSetup>
          control={control}
          name="setup"
          title="Como começar"
          options={[
            {
              value: 'COPY_FROM_OWNER',
              label: `Mesmos ${terms.servicePlural} e horários que os meus`,
              meaning: 'Já dá para marcar hoje. Cada um ajusta depois.',
            },
            {
              value: 'EMPTY',
              label: 'Começar do zero',
              meaning: `${p('O')} ${terms.professional} monta a própria agenda.`,
            },
          ]}
        />
      </View>

      <View className="items-center gap-3 py-2" style={{ display: step === 3 ? 'flex' : 'none' }}>
        <View className="h-12 w-12 items-center justify-center rounded-full bg-emerald-50 dark:bg-success-500">
          <Ionicons className="text-emerald-600 dark:text-white" name="checkmark" size={24} />
        </View>
        <ThemedText className="text-center font-semibold text-[15px] text-neutral-900">
          {created?.name} já está na equipe.
        </ThemedText>
        <ThemedText className="text-center text-sm leading-5 text-neutral-500">
          Mande o convite para{' '}
          {created?.invitePhone ? formatBrazilPhone(created.invitePhone) : 'o WhatsApp'}: com ele,{' '}
          {created?.name?.split(' ')[0]} entra no app sem senha.
        </ThemedText>
      </View>
    </TeamSheet>
  );
}
